import { useState, useEffect, useCallback } from 'react';
import { MockVideo } from '@/hooks/video-generator/useVideoGeneration';

interface VideoDetailConfig {
  preloading?: {
    enabled: boolean;
    strategy: 'aggressive' | 'conservative' | 'smart';
    preloadDistance: number;
  };
  navigation?: {
    wheel: boolean;
    keyboard: boolean;
    clickZones: boolean;
  };
  mobile?: {
    fullscreen: boolean;
    sidebarPosition: 'bottom' | 'overlay' | 'hidden';
  };
}

interface UseSmartVideoNavigationProps<T = MockVideo> {
  currentItem: T | null;
  allItems: T[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  loadMore?: () => void;
  onNavigate?: (item: T) => void;
  getItemId: (item: T) => string;
  config: VideoDetailConfig;
}

interface UseSmartVideoNavigationReturn {
  hasPrevious: boolean;
  hasNext: boolean;
  canNavigateNext: boolean;
  handlePrevious: () => void;
  handleNext: () => void;
  handleWheel: (e: React.WheelEvent) => void;
}

export const useSmartVideoNavigation = <T = MockVideo>({
  currentItem,
  allItems,
  hasNextPage,
  isFetchingNextPage,
  loadMore,
  onNavigate,
  getItemId,
  config
}: UseSmartVideoNavigationProps<T>): UseSmartVideoNavigationReturn => {
  const [currentIndex, setCurrentIndex] = useState(-1);

  // Find current item index
  useEffect(() => {
    if (!currentItem || !allItems.length) {
      setCurrentIndex(-1);
      return;
    }

    const currentId = getItemId(currentItem);
    const index = allItems.findIndex(item => getItemId(item) === currentId);
    setCurrentIndex(index);
  }, [currentItem, allItems, getItemId]);

  // Navigation state
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allItems.length - 1;
  const canNavigateNext = hasNext || hasNextPage;

  // Handle previous navigation
  const handlePrevious = useCallback(() => {
    if (!hasPrevious || !onNavigate) return;
    
    const previousItem = allItems[currentIndex - 1];
    if (previousItem) {
      onNavigate(previousItem);
    }
  }, [hasPrevious, onNavigate, allItems, currentIndex]);

  // Handle next navigation with smart loading
  const handleNext = useCallback(() => {
    if (!onNavigate) return;

    if (hasNext) {
      // Navigate to next item in current list
      const nextItem = allItems[currentIndex + 1];
      if (nextItem) {
        onNavigate(nextItem);
      }
    } else if (hasNextPage && !isFetchingNextPage && loadMore) {
      // Load more items if we're at the end
      loadMore();
    }
  }, [hasNext, hasNextPage, isFetchingNextPage, onNavigate, allItems, currentIndex, loadMore]);

  // Handle wheel navigation
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!config.navigation?.wheel) return;

    e.preventDefault();
    
    const deltaY = e.deltaY;
    const threshold = 50;

    if (deltaY > threshold) {
      // Scroll down = next
      handleNext();
    } else if (deltaY < -threshold) {
      // Scroll up = previous
      handlePrevious();
    }
  }, [config.navigation?.wheel, handleNext, handlePrevious]);

  // Keyboard navigation
  useEffect(() => {
    if (!config.navigation?.keyboard) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          handleNext();
          break;
        case 'Escape':
          // Could close the lightbox, but that's handled by the parent component
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [config.navigation?.keyboard, handlePrevious, handleNext]);

  return {
    hasPrevious,
    hasNext,
    canNavigateNext,
    handlePrevious,
    handleNext,
    handleWheel
  };
}; 